import { Options } from '../options.interface';
import { FIBII_API_CONFIG } from './fibii-api.config';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ContactData, LocaleEnum, Order, OrderStepPayload, OrderSteps } from '@wizbii/utils/models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FibiiOrdersWebservice {
  readonly #httpClient = inject(HttpClient);
  readonly #fileApiConfig = inject(FIBII_API_CONFIG);

  readonly #baseUrl = `${this.#fileApiConfig.baseUrl}/orders`;

  /**
   * Create a Money Order
   *
   * 201: Order created
   * 409: Order already exists
   * 425: No State Machine configured
   * @param userId
   * @param locale
   * @param options
   */
  createOrders(userId: string, locale: LocaleEnum, options?: Options): Observable<Order> {
    return this.#httpClient.post<Order>(`${this.#baseUrl}/`, { userId, locale }, options);
  }

  /**
   * Get an order by its id
   *
   * @param orderId
   * @param options
   */
  getOrder(orderId: string, options?: Options): Observable<Order> {
    return this.#httpClient.get<Order>(`${this.#baseUrl}/${orderId}`, options);
  }

  /**
   * Update an order
   *
   * @param orderId
   * @param payload
   * @param options
   */
  updateOrder(
    orderId: string,
    payload: { suggestionIds?: string[]; contactData?: ContactData },
    options?: Options
  ): Observable<Order> {
    return this.#httpClient.patch<Order>(`${this.#baseUrl}/${orderId}`, payload, options);
  }

  /**
   * Get available steps
   *
   * @param orderId
   * @param options
   */
  getSteps(orderId: string, options?: Options): Observable<OrderSteps> {
    return this.#httpClient.get<OrderSteps>(`${this.#baseUrl}/${orderId}/get-steps`, options);
  }

  /**
   * Go to a step by providing required step data
   *
   * @param orderId
   * @param stepPayload
   * @param options
   */
  goToStep(orderId: string, stepPayload: OrderStepPayload, options?: Options): Observable<Order> {
    return this.#httpClient.post<Order>(`${this.#baseUrl}/${orderId}/go-to-step`, stepPayload, options);
  }

  /**
   * Get user last order
   * @param userId
   * @param country Two uppercase letters (ISO 3166-1 alpha-2 country code)
   * @param options
   */
  getLastOrder(userId: string, country: string, options?: Options): Observable<Order> {
    return this.#httpClient.get<Order>(`${this.#baseUrl}/last/users/${userId}`, {
      ...options,
      params: {
        ...options?.params,
        country,
      },
    });
  }

  /**
   * Get all orders of a user
   * @param userId
   * @param country Two uppercase letters (ISO 3166-1 alpha-2 country code)
   * @param options
   */
  getAllOrders(userId: string, country: string, options?: Options): Observable<Order[]> {
    return this.#httpClient.get<Order[]>(`${this.#baseUrl}/all/users/${userId}`, {
      ...options,
      params: {
        ...options?.params,
        country,
      },
    });
  }

  sendAcquaintanceMail(orderId: string, options?: Options): Observable<void> {
    return this.#httpClient.post<void>(`${this.#baseUrl}/${orderId}/actions/acquaintance-mail/send`, {}, options);
  }
}
